import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Check,
  Flame,
  Calendar as CalIcon,
  Cake,
  Quote,
  Sparkles,
  Cloud,
  Clock,
  Search,
  Wand2,
  HeartPulse,
  Bell,
  X,
  CalendarPlus,
  MapPin,
} from "lucide-react";
import { AppShell } from "@/components/app-shell";
import {
  DCard,
  SectionHeader,
  Pill,
  Avatar,
  HeroCard,
  MagazineGrid,
  ComingSoon,
  Eyebrow,
} from "@/components/dryvn";
import { SmsThread } from "@/components/sms-thread";
// Screen migration (2026-07-05): data now flows through the adapter-
// backed hooks (demo mode serves the same fixtures through them).
// formatCAD stays — it is a pure formatter, not data.
import { formatCAD } from "@/data/mock";
// LANE 2 live-gating (2026-07-10): sections that render fixtures or hit
// null-in-live briefing fields (weather/grounded-search), or whose
// interactivity doesn't persist yet, are demo-only. Live shows only
// verified-working surfaces.
import { IS_DEMO } from "@/lib/mode";
import {
  useBriefing,
  useNudges,
  useNudgeAction,
  useHabits,
  useToggleHabit,
  useBills,
  useImportantDates,
  useReminders,
  useFamilyEvents,
  useFamily,
  useFamilyMembers,
  useMe,
  usePulse,
  useDinnerSuggestion,
  useAddIngredients,
} from "@/lib/queries";
import { CollapsibleList } from "@/components/collapsible-list";

/**
 * Rows an overview card shows before "Show all". Tighter than the hub
 * pages' threshold of 8 — this screen is a glance, not a working surface
 * (LIST_ARCHIVE_POLICY_2026-08 allows the tighter fixed slice here).
 */
const OVERVIEW_SLICE = 5;

/** Next occurrence of a YYYY-MM-DD birthday, and the age they turn. */
function nextBirthday(dob: string, now: Date): { when: Date; turning: number } | null {
  const [y, m, d] = dob.split("-").map(Number);
  if (!y || !m || !d) return null;
  let when = new Date(now.getFullYear(), m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (when < today) when = new Date(now.getFullYear() + 1, m - 1, d);
  return { when, turning: when.getFullYear() - y };
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Today — Dryvn Life" },
      {
        name: "description",
        content: "Your morning briefing, any time — habits, bills, family, and reminders.",
      },
    ],
  }),
  component: Today,
});

function Today() {
  const { data: briefing } = useBriefing();
  const { data: hookHabits } = useHabits();
  const { data: hookReminders } = useReminders();
  const { data: nudges } = useNudges();
  const { data: bills } = useBills();
  const { data: importantDates } = useImportantDates();
  const { data: familyEvents } = useFamilyEvents();
  const { data: family } = useFamily();
  const nudgeAction = useNudgeAction();
  const toggleHabit = useToggleHabit();
  const { data: me } = useMe();
  const isViewer = me?.role === "viewer";
  const { data: pulse } = usePulse(!isViewer);
  const { data: memberProfiles } = useFamilyMembers();
  const dinner = useDinnerSuggestion();
  // Dishes already suggested this session — fed back as the avoid list so
  // "Another idea" genuinely varies (capped at the last 10).
  const [seenDinners, setSeenDinners] = useState<string[]>([]);
  const suggestDinner = (recordCurrent: boolean) => {
    let exclude = seenDinners;
    const current = dinner.data?.suggestion?.title;
    if (recordCurrent && current) {
      exclude = [...seenDinners, current].slice(-10);
      setSeenDinners(exclude);
    }
    dinner.mutate(exclude);
  };
  const addIngredients = useAddIngredients();

  // Milestones — derived from real data, computed in view: next birthdays
  // (member profiles' DOB), live habit streaks. No invented entries.
  const now = new Date();
  const birthdayMilestones = memberProfiles
    .flatMap((m) => {
      if (!m.dateOfBirth) return [];
      const b = nextBirthday(m.dateOfBirth, now);
      if (!b) return [];
      // A profile whose stored birth year is the current/future year (e.g.
      // a birthday entered without a real year) computes "turns 0" — skip
      // rather than display nonsense.
      if (b.turning <= 0) return [];
      const days = Math.round((b.when.getTime() - now.getTime()) / 86_400_000);
      if (days > 60) return [];
      return [
        {
          id: `bday-${m.id}`,
          kind: "birthday" as const,
          text: `${m.name} turns ${b.turning}`,
          when:
            days <= 0
              ? "today 🎂"
              : b.when.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
          days,
        },
      ];
    })
    .sort((a, b) => a.days - b.days);
  const streakMilestones = hookHabits
    .filter((h) => h.streak >= 3)
    .sort((a, b) => b.streak - a.streak)
    .slice(0, 2)
    .map((h) => ({
      id: `streak-${h.id}`,
      kind: "streak" as const,
      text: `${h.name} — ${h.streak}-day streak`,
      when: "keep it going",
      days: 0,
    }));
  const milestones = [...birthdayMilestones, ...streakMilestones].slice(0, 5);

  const { greetingName, todayLabel, glance } = briefing;
  const quoteOfTheDay = briefing.quote;
  const memberById = (id: string) => [...family.parents, ...family.kids].find((m) => m.id === id);

  // Local interaction state (the vendored UX): optimistic toggles +
  // booked/dismissed pills layered over hook data.
  const [habitDone, setHabitDone] = useState<Record<string, boolean>>({});
  const [reminderDone, setReminderDone] = useState<Record<string, boolean>>({});
  const [scheduled, setScheduled] = useState<Record<string, "booked" | "dismissed">>({});

  const habits = hookHabits.map((h) =>
    habitDone[h.id] !== undefined ? { ...h, done: habitDone[h.id]! } : h,
  );
  const reminders = hookReminders.map((r) =>
    reminderDone[r.id] !== undefined ? { ...r, done: reminderDone[r.id]! } : r,
  );
  const todayIdx = (new Date().getDay() + 6) % 7;
  const todayEvents = familyEvents.filter((e) => e.day === todayIdx);

  const dueSoon = bills.filter((b) => !b.paid && b.daysUntil <= 5).slice(0, 3);
  const upcoming = importantDates.slice(0, 3);
  const topHabits = habits.slice(0, 5);
  const habitsDone = topHabits.filter((h) => h.done).length;
  const personalNudges = nudges.filter((n) => n.category !== "local");
  const localNudges = nudges.filter((n) => n.category === "local").slice(0, 3);

  return (
    <AppShell>
      <HeroCard
        eyebrow="Your morning briefing"
        title={
          <>
            Good morning,{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              {greetingName}
            </em>
            .
          </>
        }
      >
        <p className="max-w-lg text-sm leading-relaxed" style={{ color: "var(--dryvn-ink)" }}>
          <span className="font-mono text-xs" style={{ color: "var(--dryvn-primary-deep)" }}>
            {todayLabel}
          </span>
          <span className="mx-2 opacity-40">·</span>
          {habitsDone}/{topHabits.length} habits, {dueSoon.length} bills due soon
          {todayEvents.length > 0
            ? `, ${todayEvents.length} family event${todayEvents.length > 1 ? "s" : ""} today`
            : ""}
          .
        </p>

        <figure
          className="mt-4 max-w-xl rounded-2xl p-4"
          style={{
            backgroundColor: "color-mix(in oklab, var(--dryvn-life) 22%, transparent)",
            border: "1px solid color-mix(in oklab, var(--dryvn-primary) 30%, transparent)",
          }}
        >
          <div className="mb-1 flex items-center gap-2">
            <Quote className="h-3.5 w-3.5" style={{ color: "var(--dryvn-primary-deep)" }} />
            <Eyebrow>Today's spark</Eyebrow>
          </div>
          <blockquote
            className="font-display text-lg leading-snug"
            style={{ color: "var(--dryvn-ink)" }}
          >
            "{quoteOfTheDay.text}"
          </blockquote>
          <figcaption className="mt-1 text-xs" style={{ color: "var(--dryvn-grey)" }}>
            — {quoteOfTheDay.author}
          </figcaption>
        </figure>
      </HeroCard>

      {/* Family Pulse + Milestones — the Life faces of the Pro dashboard's
          Business Health Score and milestone rail. Pulse is adult-only
          (server-enforced); milestones are derived in view from real data. */}
      {!IS_DEMO && (
        <section className="mt-8 grid gap-3 lg:grid-cols-2">
          {!isViewer && pulse && (
            <div>
              <SectionHeader eyebrow="Family Pulse" title="How the household's doing" />
              <DCard>
                {pulse.score === null ? (
                  <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                    Not enough data yet — add a bill or a habit and the pulse starts beating.
                  </p>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3">
                      <span
                        className="font-display text-5xl"
                        style={{ color: "var(--dryvn-primary-deep)" }}
                      >
                        {pulse.score}
                      </span>
                      <span className="font-mono text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        / 100
                      </span>
                      <Pill
                        tone={pulse.score >= 80 ? "life" : pulse.score >= 55 ? "muted" : "amber"}
                      >
                        {pulse.label}
                      </Pill>
                    </div>
                    <ul className="mt-4 space-y-2">
                      {pulse.drivers.map((d) => (
                        <li key={d.key} className="flex items-center justify-between gap-3 text-xs">
                          <span style={{ color: "var(--dryvn-ink)" }}>{d.label}</span>
                          <span className="text-right" style={{ color: "var(--dryvn-grey)" }}>
                            {d.detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </DCard>
            </div>
          )}
          <div>
            <SectionHeader eyebrow="Milestones" title="Coming up for the family" />
            <DCard>
              {milestones.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                  Nothing on the horizon yet — birthdays appear once family profiles have them, and
                  streaks show up after three days in a row.
                </p>
              ) : (
                <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                  {milestones.map((ms) => (
                    <li key={ms.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                      {ms.kind === "birthday" ? (
                        <Cake
                          className="h-4 w-4 shrink-0"
                          style={{ color: "var(--dryvn-primary)" }}
                        />
                      ) : (
                        <Flame
                          className="h-4 w-4 shrink-0"
                          style={{ color: "var(--dryvn-amber)" }}
                        />
                      )}
                      <span className="flex-1 text-sm" style={{ color: "var(--dryvn-ink)" }}>
                        {ms.text}
                      </span>
                      <span className="font-mono text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        {ms.when}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </DCard>
          </div>
          {/* Tonight's dinner — one on-demand suggestion, personalized from
            real household data (member notes carry allergies, plus what's
            already on the shopping list). Nothing renders until the tap;
            model-down shows the backend's honest "kitchen's closed" copy. */}
          <div>
            <SectionHeader eyebrow="Kitchen" title="Tonight's dinner" />
            <DCard>
              {dinner.isPending ? (
                <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                  Thinking about what the family would like…
                </p>
              ) : dinner.isError ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                    Couldn't reach the kitchen. Give it another try in a moment.
                  </p>
                  <button
                    onClick={() => suggestDinner(false)}
                    className="rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
                    style={{ border: "1px solid var(--dryvn-line)", color: "var(--dryvn-ink)" }}
                  >
                    Try again
                  </button>
                </div>
              ) : dinner.data == null ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                    One idea for tonight, cooked around your family and what's already on the list.
                  </p>
                  <button
                    onClick={() => suggestDinner(false)}
                    className="rounded-full px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "var(--dryvn-primary)" }}
                  >
                    Suggest tonight's dinner
                  </button>
                </div>
              ) : dinner.data.suggestion == null ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                    {dinner.data.message ??
                      "The kitchen's closed right now — try again in a few minutes."}
                  </p>
                  <button
                    onClick={() => suggestDinner(false)}
                    className="rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
                    style={{ border: "1px solid var(--dryvn-line)", color: "var(--dryvn-ink)" }}
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  <p
                    className="font-display text-lg leading-snug"
                    style={{ color: "var(--dryvn-ink)" }}
                  >
                    {dinner.data.suggestion.title}
                  </p>
                  <p
                    className="mt-1 text-sm leading-relaxed"
                    style={{ color: "var(--dryvn-grey)" }}
                  >
                    {dinner.data.suggestion.description}
                  </p>
                  {dinner.data.suggestion.source && (
                    <a
                      href={dinner.data.suggestion.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-xs underline underline-offset-2"
                      style={{ color: "var(--dryvn-primary-deep)" }}
                    >
                      Full recipe at {dinner.data.suggestion.source.name} ↗
                    </a>
                  )}
                  <ul className="mt-3 space-y-1">
                    {dinner.data.suggestion.ingredients.map((ing) => (
                      <li
                        key={ing.name}
                        className="flex items-baseline justify-between gap-3 text-xs"
                      >
                        <span style={{ color: "var(--dryvn-ink)" }}>{ing.name}</span>
                        <span className="font-mono" style={{ color: "var(--dryvn-grey)" }}>
                          {ing.quantity}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {addIngredients.isSuccess ? (
                      <Pill tone="life">
                        <Check className="mr-1 h-3 w-3" /> On the shopping list
                      </Pill>
                    ) : (
                      <button
                        onClick={() => addIngredients.mutate(dinner.data!.suggestion!.ingredients)}
                        disabled={addIngredients.isPending}
                        className="rounded-full px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                        style={{ backgroundColor: "var(--dryvn-primary)" }}
                      >
                        {addIngredients.isPending ? "Adding…" : "Add ingredients to the list"}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        addIngredients.reset();
                        suggestDinner(true);
                      }}
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
                      style={{ border: "1px solid var(--dryvn-line)", color: "var(--dryvn-ink)" }}
                    >
                      Another idea
                    </button>
                  </div>
                  {addIngredients.isError && (
                    <p className="mt-2 text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                      Couldn't add those just now — the list is unchanged. Try again.
                    </p>
                  )}
                </>
              )}
            </DCard>
          </div>
        </section>
      )}

      {/* Vertex AI — at a glance. Weather, local time, and Grounded Search
          ship as null in the v1 briefing (external-API supervised sessions),
          so the glance cards are demo-only until those land. The agent
          "noticed for you" card below is backed by the real /nudges feed. */}
      <section className="mt-8">
        {IS_DEMO && (
          <>
            <SectionHeader
              eyebrow="Vertex AI"
              title="At a glance"
              action={
                <Pill tone="primary">
                  <Sparkles className="mr-1 h-3 w-3" /> Live
                </Pill>
              }
            />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DCard className="flex items-center gap-3">
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--dryvn-sky-soft) 80%, transparent)",
                  }}
                >
                  <Clock className="h-5 w-5" style={{ color: "var(--dryvn-sky-deep)" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <Eyebrow>Local time</Eyebrow>
                  <p
                    className="font-display text-2xl leading-none"
                    style={{ color: "var(--dryvn-ink)" }}
                  >
                    {glance.timeLabel}
                  </p>
                  <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                    {glance.weather.city}
                  </p>
                </div>
              </DCard>

              <DCard className="flex items-center gap-3">
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--dryvn-warm) 30%, transparent)",
                  }}
                >
                  <Cloud className="h-5 w-5" style={{ color: "var(--dryvn-warm-deep)" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <Eyebrow>Weather</Eyebrow>
                  <p
                    className="font-display text-2xl leading-none"
                    style={{ color: "var(--dryvn-ink)" }}
                  >
                    {glance.weather.tempC}°C
                  </p>
                  <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                    {glance.weather.condition} · H {glance.weather.high}° / L {glance.weather.low}°
                  </p>
                </div>
              </DCard>

              <DCard className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4" style={{ color: "var(--dryvn-primary-deep)" }} />
                  <Eyebrow>Grounded search</Eyebrow>
                </div>
                <p className="text-sm font-medium" style={{ color: "var(--dryvn-ink)" }}>
                  {glance.search.topic}
                </p>
                <p className="text-xs leading-relaxed" style={{ color: "var(--dryvn-grey)" }}>
                  {glance.search.summary}
                </p>
              </DCard>
            </div>
          </>
        )}

        <DCard className="mt-3">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <HeartPulse className="h-4 w-4" style={{ color: "var(--dryvn-primary-deep)" }} />
              <Eyebrow>Agent · noticed for you</Eyebrow>
            </div>
            <Pill tone="warm">
              {personalNudges.filter((n) => !scheduled[n.id]).length} to review
            </Pill>
          </div>

          {/* Overview surfaces carry a tighter slice than the hub pages —
              the home screen is a glance, and five is what fits above the
              fold. Same affordance, smaller threshold. */}
          <CollapsibleList items={personalNudges} resource="suggestions" threshold={OVERVIEW_SLICE}>
            {(shownNudges) => (
              <ul className="grid gap-2 sm:grid-cols-2">
                {shownNudges.map((n) => {
                  const state = scheduled[n.id];
                  const isBooked = state === "booked";
                  const isDismissed = state === "dismissed";
                  return (
                    <li
                      key={n.id}
                      className="rounded-2xl p-3"
                      style={{
                        backgroundColor: isDismissed
                          ? "color-mix(in oklab, var(--dryvn-page-tint) 60%, transparent)"
                          : "var(--dryvn-page-tint)",
                        border: "1px solid var(--dryvn-line-soft)",
                        opacity: isDismissed ? 0.55 : 1,
                      }}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <Pill
                          tone={
                            n.category === "Health"
                              ? "life"
                              : n.category === "Movement"
                                ? "primary"
                                : "muted"
                          }
                        >
                          {n.category}
                        </Pill>
                        {isBooked && (
                          <Pill tone="life">
                            <Check className="mr-1 h-3 w-3" /> Booked
                          </Pill>
                        )}
                        {isDismissed && <Pill tone="muted">Dismissed</Pill>}
                      </div>
                      <p
                        className="text-sm font-medium leading-snug"
                        style={{ color: "var(--dryvn-ink)" }}
                      >
                        {n.headline}
                      </p>
                      <p
                        className="mt-0.5 text-[11px] leading-snug"
                        style={{ color: "var(--dryvn-grey)" }}
                      >
                        {n.reason}
                      </p>

                      <div
                        className="mt-2 flex items-center gap-2 rounded-xl px-2.5 py-2"
                        style={{
                          backgroundColor: "var(--dryvn-card)",
                          border: "1px solid var(--dryvn-line-soft)",
                        }}
                      >
                        {n.proposal.action === "book" ? (
                          <CalendarPlus
                            className="h-3.5 w-3.5 shrink-0"
                            style={{ color: "var(--dryvn-primary-deep)" }}
                          />
                        ) : (
                          <Bell
                            className="h-3.5 w-3.5 shrink-0"
                            style={{ color: "var(--dryvn-primary-deep)" }}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p
                            className="truncate text-xs font-medium"
                            style={{ color: "var(--dryvn-ink)" }}
                          >
                            {n.proposal.label}
                          </p>
                          <p
                            className="truncate font-mono text-[10px]"
                            style={{ color: "var(--dryvn-grey)" }}
                          >
                            {n.proposal.when}
                            {n.proposal.duration ? ` · ${n.proposal.duration}` : ""}
                          </p>
                        </div>
                      </div>

                      {!state && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => {
                              setScheduled((p) => ({ ...p, [n.id]: "booked" }));
                              nudgeAction.mutate({ id: n.id, action: "approve" });
                            }}
                            className="flex-1 rounded-full px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
                            style={{ backgroundColor: "var(--dryvn-primary)" }}
                          >
                            Approve & schedule
                          </button>
                          <button
                            onClick={() => {
                              setScheduled((p) => ({ ...p, [n.id]: "dismissed" }));
                              nudgeAction.mutate({ id: n.id, action: "dismiss" });
                            }}
                            aria-label="Dismiss"
                            className="grid h-7 w-7 place-items-center rounded-full transition-colors"
                            style={{
                              border: "1px solid var(--dryvn-line)",
                              color: "var(--dryvn-grey)",
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CollapsibleList>

          <p className="mt-3 text-[11px] leading-snug" style={{ color: "var(--dryvn-grey)" }}>
            <Wand2 className="mr-1 inline h-3 w-3" />
            Approvals write to your Google Calendar and confirm by SMS. Nothing is booked without
            your tap.
          </p>
        </DCard>
      </section>

      {/* Around town — Local Happenings surfaced by the agent. */}
      <section className="mt-8">
        <SectionHeader
          eyebrow="Local Happenings"
          title="Around town"
          action={
            <Pill tone="primary">
              <MapPin className="mr-1 h-3 w-3" /> Nearby
            </Pill>
          }
        />
        <DCard>
          {localNudges.length === 0 ? (
            <p className="py-6 text-center text-sm" style={{ color: "var(--dryvn-grey)" }}>
              I'll keep an eye out for things happening nearby.
            </p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {localNudges.map((n) => {
                const state = scheduled[n.id];
                const isBooked = state === "booked";
                const isDismissed = state === "dismissed";
                return (
                  <li
                    key={n.id}
                    className="rounded-2xl p-3"
                    style={{
                      backgroundColor: isDismissed
                        ? "color-mix(in oklab, var(--dryvn-page-tint) 60%, transparent)"
                        : "var(--dryvn-page-tint)",
                      border: "1px solid var(--dryvn-line-soft)",
                      opacity: isDismissed ? 0.55 : 1,
                    }}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <Pill tone="primary">
                        <MapPin className="mr-1 h-3 w-3" /> Local
                      </Pill>
                      {isBooked && (
                        <Pill tone="life">
                          <Check className="mr-1 h-3 w-3" /> Booked
                        </Pill>
                      )}
                      {isDismissed && <Pill tone="muted">Dismissed</Pill>}
                    </div>
                    <p
                      className="text-sm font-medium leading-snug"
                      style={{ color: "var(--dryvn-ink)" }}
                    >
                      {n.headline}
                    </p>
                    {n.detail && (
                      <p
                        className="mt-0.5 text-[11px] leading-snug"
                        style={{ color: "var(--dryvn-grey)" }}
                      >
                        {n.detail}
                      </p>
                    )}
                    {n.date && (
                      <p
                        className="mt-1 font-mono text-[10px]"
                        style={{ color: "var(--dryvn-primary-deep)" }}
                      >
                        {n.date}
                      </p>
                    )}

                    {!state && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => {
                            setScheduled((p) => ({ ...p, [n.id]: "booked" }));
                            nudgeAction.mutate({ id: n.id, action: "approve" });
                          }}
                          className="flex-1 rounded-full px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
                          style={{ backgroundColor: "var(--dryvn-primary)" }}
                        >
                          Approve to calendar
                        </button>
                        <button
                          onClick={() => {
                            setScheduled((p) => ({ ...p, [n.id]: "dismissed" }));
                            nudgeAction.mutate({ id: n.id, action: "dismiss" });
                          }}
                          aria-label="Dismiss"
                          className="grid h-7 w-7 place-items-center rounded-full transition-colors"
                          style={{
                            border: "1px solid var(--dryvn-line)",
                            color: "var(--dryvn-grey)",
                          }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-3 text-[11px] leading-snug" style={{ color: "var(--dryvn-grey)" }}>
            Up to 3 suggestions per week. Adjust in Settings → Notifications.
          </p>
        </DCard>
      </section>

      <div className="mt-8">
        <MagazineGrid>
          {/* Habits — spans wide */}
          <section className="lg:col-span-4">
            <SectionHeader
              eyebrow="Rituals"
              title="Habits today"
              action={
                <Pill tone="life">
                  {habitsDone}/{topHabits.length}
                </Pill>
              }
            />
            <DCard className="p-3">
              <ul>
                {topHabits.map((h) => (
                  <li key={h.id}>
                    <button
                      onClick={() => {
                        setHabitDone((p) => ({ ...p, [h.id]: !h.done }));
                        toggleHabit.mutate(h.id);
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-[var(--dryvn-page-tint)]"
                    >
                      <span
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 transition-colors"
                        style={{
                          borderColor: h.done ? "var(--dryvn-primary)" : "var(--dryvn-line)",
                          backgroundColor: h.done ? "var(--dryvn-primary)" : "transparent",
                        }}
                      >
                        {h.done && <Check className="h-3.5 w-3.5 text-white" />}
                      </span>
                      <span
                        className="flex-1 text-sm"
                        style={{
                          color: h.done ? "var(--dryvn-grey)" : "var(--dryvn-ink)",
                          textDecoration: h.done ? "line-through" : "none",
                        }}
                      >
                        {h.name}
                      </span>
                      {h.streak > 0 && (
                        <span
                          className="flex items-center gap-1 text-xs"
                          style={{ color: "var(--dryvn-grey)" }}
                        >
                          <Flame
                            className="h-3.5 w-3.5"
                            style={{ color: "var(--dryvn-warm-deep)" }}
                          />
                          {h.streak}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </DCard>
          </section>

          {/* Bills — spans narrow */}
          <section className="lg:col-span-2 lg:pt-8">
            <SectionHeader eyebrow="Money" title="Bills due soon" />
            <DCard>
              <ul className="space-y-3">
                {dueSoon.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p
                        className="truncate font-medium text-sm"
                        style={{ color: "var(--dryvn-ink)" }}
                      >
                        {b.name}
                      </p>
                      <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        in {b.daysUntil}d · day {b.dueDay}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono text-sm" style={{ color: "var(--dryvn-ink)" }}>
                        {formatCAD(b.amountCents)}
                      </p>
                      {b.daysUntil <= 2 && <Pill tone="amber">soon</Pill>}
                    </div>
                  </li>
                ))}
              </ul>
            </DCard>
          </section>

          {/* Reminders & tasks — demo-only: the check-off toggle is local
              state that does not persist to the backend yet (coming-soon).
              Hidden in live so a stranger never taps a checkbox that
              silently resets. */}
          {IS_DEMO && (
            <section className="lg:col-span-3 lg:pt-4">
              <SectionHeader eyebrow="Today" title="Reminders & tasks" action={<ComingSoon />} />
              <CollapsibleList items={reminders} resource="reminders" threshold={OVERVIEW_SLICE}>
                {(shownReminders) => (
                  <DCard className="p-3">
                    <ul>
                      {shownReminders.map((r) => (
                        <li key={r.id}>
                          <button
                            onClick={() => setReminderDone((p) => ({ ...p, [r.id]: !r.done }))}
                            className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-[var(--dryvn-page-tint)]"
                          >
                            <span
                              className="grid h-5 w-5 shrink-0 place-items-center rounded-full border-2"
                              style={{
                                borderColor: r.done ? "var(--dryvn-primary)" : "var(--dryvn-line)",
                                backgroundColor: r.done ? "var(--dryvn-primary)" : "transparent",
                              }}
                            >
                              {r.done && <Check className="h-3 w-3 text-white" />}
                            </span>
                            <span
                              className="flex-1 text-sm"
                              style={{
                                color: r.done ? "var(--dryvn-grey)" : "var(--dryvn-ink)",
                                textDecoration: r.done ? "line-through" : "none",
                              }}
                            >
                              {r.title}
                            </span>
                            <span
                              className="font-mono text-[11px]"
                              style={{ color: "var(--dryvn-grey)" }}
                            >
                              {r.time}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          )}

          {/* Coming up — spans narrow */}
          <section className="lg:col-span-3 lg:pt-4">
            <SectionHeader eyebrow="Ahead" title="Coming up" />
            <DCard>
              <ul className="space-y-3">
                {upcoming.map((d) => (
                  <li key={d.id} className="flex items-center gap-3">
                    <span
                      className="grid h-10 w-10 place-items-center rounded-full"
                      style={{
                        backgroundColor: "color-mix(in oklab, var(--dryvn-warm) 22%, transparent)",
                      }}
                    >
                      {d.type === "birthday" ? (
                        <Cake className="h-4 w-4" style={{ color: "var(--dryvn-warm-deep)" }} />
                      ) : (
                        <CalIcon className="h-4 w-4" style={{ color: "var(--dryvn-warm-deep)" }} />
                      )}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: "var(--dryvn-ink)" }}>
                        {d.title}
                      </p>
                      <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        in {d.daysUntil} days
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </DCard>
          </section>

          {/* Family today — full width */}
          <section className="lg:col-span-6">
            <SectionHeader eyebrow="Household" title="Family today" />
            <CollapsibleList items={todayEvents} resource="events" threshold={OVERVIEW_SLICE}>
              {(shownToday) => (
                <DCard>
                  <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {shownToday.map((e) => {
                      const m = memberById(e.memberId);
                      return (
                        <li
                          key={e.id}
                          className="flex items-center gap-3 rounded-xl p-3"
                          style={{ backgroundColor: "var(--dryvn-page-tint)" }}
                        >
                          {m && <Avatar initials={m.initials} color={m.color} />}
                          <div className="min-w-0 flex-1">
                            <p
                              className="truncate text-sm font-medium"
                              style={{ color: "var(--dryvn-ink)" }}
                            >
                              {e.title}
                            </p>
                            <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                              {m?.name} · {e.time}
                            </p>
                          </div>
                          <Pill tone="life">{e.kind}</Pill>
                        </li>
                      );
                    })}
                  </ul>
                </DCard>
              )}
            </CollapsibleList>
          </section>

          {/* SMS thread — demo-only showcase: renders a fixture conversation
              and the placeholder DRYVN_NUMBER. Hidden in live so a stranger
              never sees a scripted thread or texts a non-routed number. */}
          {IS_DEMO && (
            <section className="lg:col-span-6">
              <div className="mb-2">
                <Eyebrow>The interface</Eyebrow>
              </div>
              <SmsThread />
            </section>
          )}
        </MagazineGrid>
      </div>
    </AppShell>
  );
}
